import React from "react";
import { Button, TextField } from "@shopify/polaris";
import type { IconSource } from "@shopify/polaris";
import { DeleteIcon, PlusIcon } from "@shopify/polaris-icons";
import { Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import useIsPhone from "features/files/use-is-phone";

interface OrderedSecurityRule {
  ruleText: string;
  order: number;
}

interface WorkSecurityRulesEditorProps {
  rules: string[];
  onRulesChange: (rules: string[]) => void;
  disabled?: boolean;
  maxHeightClassName?: string;
}

const addRuleIcon = PlusIcon as unknown as IconSource;
const deleteRuleIcon = DeleteIcon as unknown as IconSource;

export function buildOrderedSecurityRules(
  rules: string[]
): OrderedSecurityRule[] {
  return rules
    .map((r) => r.trim())
    .filter((r) => r.length > 0)
    .map((ruleText, index) => ({
      ruleText,
      order: index + 1,
    }));
}

export default function WorkSecurityRulesEditor({
  rules,
  onRulesChange,
  disabled,
  maxHeightClassName = "md:max-h-[250px]",
}: WorkSecurityRulesEditorProps) {
  const { t } = useTranslation();
  const isPhone = useIsPhone();
  const removeLabel = t("remove", { defaultValue: "Remove" });

  const handleAddRuleField = () => {
    onRulesChange([...rules, ""]);
  };

  const handleRuleChange = (index: number, value: string) => {
    const updated = [...rules];
    // A phone shows a rule in a two-line box; a rule is one sentence, so Enter must not add a line break.
    updated[index] = value.replace(/\s*\n\s*/g, " ");
    onRulesChange(updated);
  };

  const handleRemoveRuleField = (index: number) => {
    const updated = rules.filter((_, i) => i !== index);
    onRulesChange(updated.length ? updated : [""]);
  };

  return (
    <div>
      <div className="flex items-center justify-between border-b border-gray-200 pb-2 mb-2">
        <span className="text-sm font-semibold text-gray-900">
          {t("pointwise_security_rules", {
            defaultValue: "Rules (Points)",
          })}
        </span>
        <Button
          onClick={handleAddRuleField}
          disabled={disabled}
          icon={addRuleIcon}
          size="slim"
        >
          {t("add_rule", { defaultValue: "Add Point" })}
        </Button>
      </div>

      <div className={`${maxHeightClassName} space-y-3 pr-1 md:overflow-y-auto`}>
        {rules.map((rule, index) => (
          <div key={index} className="flex items-center gap-2">
            <span className="text-xs font-semibold text-gray-400 w-6">
              #{index + 1}
            </span>
            <div className="flex-1">
              <TextField
                label=""
                labelHidden
                placeholder={t("rule_text_placeholder", {
                  defaultValue:
                    "e.g., Must wear safety goggles in designated areas.",
                })}
                value={rule}
                onChange={(val) => handleRuleChange(index, val)}
                multiline={isPhone ? 2 : false}
                disabled={disabled}
                autoComplete="off"
              />
            </div>
            {isPhone ? (
              <button
                type="button"
                aria-label={removeLabel}
                onClick={() => handleRemoveRuleField(index)}
                disabled={disabled || rules.length === 1}
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md text-red-700 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-40"
              >
                <Trash2 className="h-5 w-5" aria-hidden />
              </button>
            ) : (
              <Button
                accessibilityLabel={removeLabel}
                onClick={() => handleRemoveRuleField(index)}
                disabled={disabled || rules.length === 1}
                icon={deleteRuleIcon}
                tone="critical"
                variant="plain"
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
